/** Validation status derivation from backend data */

export type ValidationStatus =
  | 'DRAFT'
  | 'CREATIVE_READY'
  | 'CAMPAIGN_ACTIVE'
  | 'CAMPAIGN_PAUSED'
  | 'INSIGHTS_AVAILABLE'
  | 'VALIDATED'
  | 'REJECTED';

export interface StatusInfo {
  status: ValidationStatus;
  label: string;
  color: string;        // Tailwind color class
  bgColor: string;      // Tailwind bg class
  textColor: string;    // Tailwind text class
  icon: string;         // Emoji or icon name
}

const STATUS_MAP: Record<ValidationStatus, Omit<StatusInfo, 'status'>> = {
  DRAFT: {
    label: 'Entwurf',
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    icon: '📝',
  },
  CREATIVE_READY: {
    label: 'Creative bereit',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    icon: '🎨',
  },
  CAMPAIGN_ACTIVE: {
    label: 'Kampagne aktiv',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    icon: '🚀',
  },
  CAMPAIGN_PAUSED: {
    label: 'Kampagne pausiert',
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    icon: '⏸️',
  },
  INSIGHTS_AVAILABLE: {
    label: 'Insights verfügbar',
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    icon: '📊',
  },
  VALIDATED: {
    label: 'Validiert',
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    icon: '✅',
  },
  REJECTED: {
    label: 'Abgelehnt',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    icon: '❌',
  },
};

export function getStatusInfo(status: ValidationStatus): StatusInfo {
  return { status, ...STATUS_MAP[status] };
}

/** Derive validation status from problem/solution + campaign data */
export function deriveStatus(item: {
  validated?: number;
  selected?: number;
  campaign_status?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  campaign_id?: string | null;
  image_url?: string | null;
}): ValidationStatus {
  // Explicit validation decision
  if (item.validated === 1) return 'VALIDATED';
  if (item.validated === -1) return 'REJECTED';

  // Has campaign insights
  if (item.impressions && item.impressions > 0) return 'INSIGHTS_AVAILABLE';

  // Campaign running
  if (item.campaign_status === 'ACTIVE') return 'CAMPAIGN_ACTIVE';
  if (item.campaign_status === 'PAUSED') return 'CAMPAIGN_PAUSED';

  // Has creative (image)
  if (item.campaign_id || item.image_url) return 'CREATIVE_READY';

  return 'DRAFT';
}
